import curl2Json from '@bany/curl-to-json'
import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { DropdownInput } from '@neo/blocks/dropdown-input'
import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { Button, ButtonWithLoader } from '@neo/index'
import { cn } from '@neo/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@neo/primitives/dialog'
import { Popover, PopoverTrigger } from '@neo/primitives/popover'
import { Select } from '@neo/primitives/select'
import { Tooltip } from '@neo/primitives/tooltip'
import type { FieldApi } from '@tanstack/react-form'
import type { AxiosError } from 'axios'
import {
  ArrowLeftRight,
  Box,
  ChevronDown,
  Code,
  Globe,
  Minus,
  Plus,
  Sparkles,
  SquareAsterisk,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useExternalOrgAppsQuery } from 'v2/pages/settings/integrations/queries'
import { useUsersQuery } from 'v2/pages/settings/queries'
import { CodeEditor } from 'v2/shared/components/code-editor'
import { flowService } from 'v2/shared/services/flow-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import { errorToast } from 'v2/utils/toasts'
import { z } from 'zod'
import { JsonKeyValueEditor } from '../../components/json-key-value-editor'
import { NodevarList } from '../../components/nodevar-list'
import { aiAgentV2ActionInitialState } from '../../config/nodes.config'
import { aiAgentV2ActionsArraySchema } from '../../config/validation.config'
import { extractFieldsAndSchemaFromAction } from '../../hooks/use-action-fields'
import {
  useGenerateFunctionSchemaMutation,
  useGenerateHttpFunctionSchemaMutation,
} from '../../queries'
import type { AiAgentV2NodeData } from '../../types'
import { decodeActionIdentifier, encodeActionIdentifier } from '../../utils/ai-action-utils'
import {
  parseJsonData,
  preprocessCurlCommand,
  processBodyData,
} from '../../utils/http-request-utils'
import {
  buildActionConnectionAccountLabels,
  buildActionOptionsForConnection,
  buildConnectionOptions,
  findActionConnectionForSavedProvider,
} from '../../utils/integration-action-options'
import { CodeGenerationModal } from './code-generation-modal-v2'
import { dropOwnVariableSlots, ownVariableNames } from './own-variable-slots'
import { SecretVariablesContent } from './secret-variables-popover-v2'

type FormValues = AiAgentV2NodeData['config']
type ActionItem = NonNullable<FormValues['actions']>[number]
// biome-ignore lint/suspicious/noExplicitAny: TanStack form array field type - exact generic params not expressible
type ActionsFieldApi = FieldApi<any, any, any, any, any>

const ACTION_TYPE_OPTIONS = [
  { label: 'Execute app action', value: 'EXECUTE_ACTION' },
  { label: 'Custom Function', value: 'CUSTOM_CODE' },
  { label: 'HTTP Request', value: 'HTTP' },
]

const HTTP_METHOD_OPTIONS = [
  { label: 'GET', value: 'get' },
  { label: 'POST', value: 'post' },
  { label: 'PUT', value: 'put' },
  { label: 'PATCH', value: 'patch' },
  { label: 'DELETE', value: 'delete' },
]

const HTTP_AUTH_TYPE_OPTIONS = [
  { label: 'No Auth', value: 'no_auth' },
  { label: 'Basic Auth', value: 'basic_auth' },
  { label: 'Bearer Token', value: 'bearer_token' },
]

const HTTP_BODY_TYPE_OPTIONS = [
  { label: 'None', value: 'none' },
  { label: 'JSON', value: 'json' },
  { label: 'Form Data', value: 'form_data' },
  { label: 'Form URL Encoded', value: 'form_urlencoded' },
]

const getActionIcon = (actionType: string, appLogoUrl?: string | null) => {
  if (actionType === 'EXECUTE_ACTION') {
    if (appLogoUrl) {
      return <img src={appLogoUrl} alt="" className="w-5 h-5 shrink-0 rounded" />
    }
    return <Box className="w-5 h-5 shrink-0" />
  }
  if (actionType === 'CUSTOM_CODE') return <Code className="w-5 h-5 shrink-0" />
  if (actionType === 'HTTP') return <Globe className="w-5 h-5 shrink-0" />
  return null
}

/** Import cURL button + dialog for HTTP actions */
const CurlImportButton = ({
  form,
  index,
}: {
  form: GenericFormType<FormValues>
  index: number
}) => {
  const [curlCommand, setCurlCommand] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [error, setError] = useState('')

  const handleImport = () => {
    try {
      setError('')
      const processed = preprocessCurlCommand(curlCommand)
      const parsed = curl2Json(processed)

      // Clear all HTTP fields before importing
      const set = (field: string, val: string) =>
        form.setFieldValue(`actions[${index}].${field}` as never, val as never)
      set('http_url', '')
      set('http_method', 'post')
      set('http_params', '')
      set('http_headers', '')
      set('http_auth_type', 'no_auth')
      set('http_auth_username', '')
      set('http_auth_password', '')
      set('http_auth_token', '')
      set('http_body_type', 'none')
      set('http_body_template', '')

      // Set URL (curl2Json already strips query params into parsed.params)
      if (parsed.url) {
        form.setFieldValue(`actions[${index}].http_url`, parsed.url)
      }

      // Query params — curl2Json extracts these from the URL
      if (parsed.params && Object.keys(parsed.params).length > 0) {
        form.setFieldValue(`actions[${index}].http_params`, JSON.stringify(parsed.params, null, 2))
      }

      // Set method — default to GET when curl has no -X flag and no body data
      const rawMethod = parsed.method?.toLowerCase()
      const hasBody = parsed.data || (parsed as unknown as Record<string, unknown>).form
      const method = (rawMethod || (hasBody ? 'post' : 'get')) as
        | 'get'
        | 'post'
        | 'put'
        | 'patch'
        | 'delete'
      form.setFieldValue(`actions[${index}].http_method`, method)

      // Auth — Authorization header
      const authHeader = parsed.header?.Authorization || parsed.header?.authorization
      if (authHeader) {
        if (authHeader.startsWith('Basic ')) {
          try {
            const decoded = atob(authHeader.split(' ')[1])
            const [username, ...rest] = decoded.split(':')
            form.setFieldValue(`actions[${index}].http_auth_type`, 'basic_auth')
            form.setFieldValue(`actions[${index}].http_auth_username`, username)
            form.setFieldValue(`actions[${index}].http_auth_password`, rest.join(':'))
          } catch {
            /* ignore decode error */
          }
        } else if (authHeader.startsWith('Bearer ')) {
          form.setFieldValue(`actions[${index}].http_auth_type`, 'bearer_token')
          form.setFieldValue(`actions[${index}].http_auth_token`, authHeader.split(' ')[1])
        }
      }

      // Auth — -u flag for basic auth
      const basicAuthMatch = curlCommand.match(/-u\s+(['"`]?)([^'"`\s]+):([^'"`\s]+)\1/)
      if (basicAuthMatch) {
        form.setFieldValue(`actions[${index}].http_auth_type`, 'basic_auth')
        form.setFieldValue(`actions[${index}].http_auth_username`, basicAuthMatch[2])
        form.setFieldValue(`actions[${index}].http_auth_password`, basicAuthMatch[3])
      }

      // Headers — exclude auth headers and Content-Type (inferred from body)
      if (parsed.header && Object.keys(parsed.header).length > 0) {
        const headers = { ...parsed.header }
        delete headers.Authorization
        delete headers.authorization
        delete headers['Content-Type']
        delete headers['content-type']
        if (Object.keys(headers).length > 0) {
          form.setFieldValue(`actions[${index}].http_headers`, JSON.stringify(headers, null, 2))
        }
      }

      // Body — handle -d (data) and -F (form) flags
      const parsedAny = parsed as unknown as Record<string, unknown>
      const formEntries = parsedAny.form as string[] | undefined
      if (formEntries && Array.isArray(formEntries) && formEntries.length > 0) {
        // -F flag → form_data: parse "key=value" pairs into object
        const formData: Record<string, string> = {}
        for (const entry of formEntries) {
          const eqIdx = entry.indexOf('=')
          if (eqIdx > 0) {
            formData[entry.slice(0, eqIdx)] = entry.slice(eqIdx + 1)
          }
        }
        form.setFieldValue(`actions[${index}].http_body_type`, 'form_data')
        form.setFieldValue(
          `actions[${index}].http_body_template`,
          JSON.stringify(formData, null, 2),
        )
      } else if (parsed.data != null) {
        // Check if raw cURL used -d with key=value format (not JSON)
        const rawDataMatch = curlCommand.match(/-d\s+['"`]([^'"`]+)['"`]/)
        const rawData = rawDataMatch?.[1]
        const isFormUrlEncoded = rawData && !rawData.trim().startsWith('{') && rawData.includes('=')

        if (isFormUrlEncoded) {
          form.setFieldValue(`actions[${index}].http_body_type`, 'form_urlencoded')
          form.setFieldValue(
            `actions[${index}].http_body_template`,
            JSON.stringify(parsed.data, null, 2),
          )
        } else {
          const { bodyType, bodyData } = processBodyData(parsed.data)
          if (bodyType !== 'none') {
            form.setFieldValue(`actions[${index}].http_body_type`, bodyType)
            form.setFieldValue(`actions[${index}].http_body_template`, bodyData)
          }
        }
      }

      setIsOpen(false)
      setCurlCommand('')
    } catch {
      setError('Invalid cURL command. Please check the format and try again.')
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="font-inter text-xs-500 text-primary hover:underline mt-1 self-end"
        >
          Import cURL
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[444px] max-h-[600px] flex flex-col overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import cURL</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2 py-2">
          <CodeEditor
            value={curlCommand}
            onChange={setCurlCommand}
            className="border border-st2 rounded-md overflow-auto min-h-[150px]"
          />
          {error && <p className="text-xs-400 text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleImport} disabled={!curlCommand.trim()}>
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Validate actions using the Zod schema and set field-level errors. Returns true if valid. */
export const validateAiAgentV2Actions = (form: GenericFormType<FormValues>): boolean => {
  const actions = form.state.values.actions ?? []
  const result = aiAgentV2ActionsArraySchema.safeParse(actions)

  if (result.success) return true

  for (const issue of result.error.issues) {
    const [index, field] = issue.path as [number, string]
    if (typeof index === 'number' && typeof field === 'string') {
      // biome-ignore lint/suspicious/noExplicitAny: dynamic field paths for setFieldMeta
      const fieldName = `actions[${index}].${field}` as any
      form.setFieldMeta(fieldName, (prev) => ({
        ...prev,
        errorMap: { onChange: issue.message },
        errors: [issue.message],
      }))
    }
  }

  return false
}

export const AiAgentV2Actions = ({
  form,
  nodeVars,
  isEditing = true,
  ownVariables = false,
}: {
  form: GenericFormType<FormValues>
  nodeVars?: string[]
  isEditing?: boolean
  ownVariables?: boolean
}) => {
  const { data: activeApps, isLoading: isLoadingApps } = useExternalOrgAppsQuery(true)
  // V2.9 and v2.10 on a call: the runner fills a request's {{name}} from the Variable `name`
  const rows = form.useStore((s) => s.values.extract_variables)
  const channel = form.useStore((s) => s.values.channel)
  // only a name the runner's {{slot}} can match
  const requestVars = useMemo(
    () => [
      ...(nodeVars ?? []),
      ...ownVariableNames({ channel, extract_variables: rows }, ownVariables).flatMap((name) =>
        /^\w+$/.test(name) ? [`{{${name}}}`] : [],
      ),
    ],
    [nodeVars, rows, channel, ownVariables],
  )

  return (
    <form.Field name="actions" mode="array">
      {(field) => {
        const actions = (field.state.value ?? []) as ActionItem[]
        return (
          <div className="flex flex-col gap-4">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-fit gap-2"
              data-testid="button-add-action"
              disabled={!isEditing}
              onClick={() => {
                const newAction = aiAgentV2ActionInitialState(actions.length)
                field.pushValue(newAction as never)
              }}
            >
              <Plus className="h-3.5 w-3.5" />
              Define actions
            </Button>
            {actions.map((action, index) => (
              <ActionCard
                key={action.id}
                form={form}
                field={field}
                index={index}
                nodeVars={nodeVars}
                requestVars={requestVars}
                ownVariables={ownVariables}
                activeApps={activeApps}
                isLoadingApps={isLoadingApps}
              />
            ))}
          </div>
        )
      }}
    </form.Field>
  )
}

const getActionDisplayName = (
  actionType: string | null | undefined,
  actionName: string | null | undefined,
  codeName: string | null | undefined,
  httpToolName: string | null | undefined,
  index: number,
): string => {
  if (actionType === 'EXECUTE_ACTION' && actionName?.trim()) {
    return actionName.trim()
  }
  if (actionType === 'CUSTOM_CODE' && codeName?.trim()) {
    return codeName.trim()
  }
  if (actionType === 'HTTP' && httpToolName?.trim()) {
    return httpToolName.trim()
  }
  return `Action ${index + 1}`
}

const ActionCard = ({
  form,
  field,
  index,
  nodeVars,
  requestVars,
  ownVariables,
  activeApps,
  isLoadingApps,
}: {
  form: GenericFormType<FormValues>
  field: ActionsFieldApi
  index: number
  nodeVars?: string[]
  requestVars: string[]
  ownVariables: boolean
  activeApps: RExternalOrgApp[] | undefined
  isLoadingApps: boolean
}) => {
  const { mutate: generateFunctionSchema, isPending: isGeneratingFunctionSchema } =
    useGenerateFunctionSchemaMutation()
  const { mutate: generateHttpFunctionSchema, isPending: isGeneratingHttpFunctionSchema } =
    useGenerateHttpFunctionSchemaMutation()
  // Use narrow primitive selectors to avoid re-renders when code_body/code_function_schema change
  const actionExists = form.useStore((s) => !!s.values.actions?.[index])
  const actionId = form.useStore((s) => s.values.actions?.[index]?.id)
  const isExpanded = form.useStore((s) => s.values.actions?.[index]?.is_expanded) ?? true
  const actionType = form.useStore((s) => s.values.actions?.[index]?.action_type)
  const actionName = form.useStore((s) => s.values.actions?.[index]?.action_name)
  const codeName = form.useStore((s) => s.values.actions?.[index]?.code_name)
  const httpToolName = form.useStore((s) => s.values.actions?.[index]?.http_tool_name)
  const httpToolDescription = form.useStore((s) => s.values.actions?.[index]?.http_tool_description)
  const appProvider = form.useStore((s) => s.values.actions?.[index]?.app_provider)
  const orgAppUuid = form.useStore((s) => s.values.actions?.[index]?.org_app_uuid)
  const codeDescription = form.useStore((s) => s.values.actions?.[index]?.code_description)
  const actionInstructions = form.useStore((s) => s.values.actions?.[index]?.action_instructions)
  const [isSecretVarsOpen, setIsSecretVarsOpen] = useState(false)
  const [isCodeGenModalOpen, setIsCodeGenModalOpen] = useState(false)

  // Resolve connecting-user names (same as the connections list) so multiple connections of the
  // same app are distinguishable in the dropdown by their owner when there's no account identifier.
  const { data: usersData } = useUsersQuery()
  const userMap = useMemo(() => {
    const m: Record<string, string> = {}
    for (const u of usersData?.members_list ?? []) m[u.aom_uuid] = u.account_name
    return m
  }, [usersData])

  const getCurrentActionIndex = () =>
    form.state.values.actions?.findIndex((a) => a.id === actionId) ?? -1

  // Actions saved before org_app_uuid existed carry only app_provider; bind them to a connection.
  useEffect(() => {
    if (typeof actionName === 'string' && appProvider && !orgAppUuid) {
      const appByProvider = findActionConnectionForSavedProvider(activeApps, appProvider)
      if (appByProvider) {
        form.setFieldValue(`actions[${index}].org_app_uuid`, appByProvider.id)
      }
    }
  }, [actionName, appProvider, orgAppUuid, activeApps, form, index])

  const appLogoUrl = useMemo(() => {
    if (actionType !== 'EXECUTE_ACTION' || !orgAppUuid) return null
    const app = (activeApps || []).find((a) => a.id === orgAppUuid)
    return app?.logo_url || null
  }, [actionType, orgAppUuid, activeApps])

  const getCollapsedDescription = (): string => {
    if (actionType === 'EXECUTE_ACTION') {
      if (!actionName) return 'NO ACTION SELECTED!!'
      return actionInstructions || ''
    }
    if (actionType === 'CUSTOM_CODE') return codeDescription || ''
    if (actionType === 'HTTP') return httpToolDescription || ''
    return ''
  }

  // The user picks the connection, then the action list narrows to it. org_app_uuid
  // already IS the connection, so this is a UI split rather than a new field.
  const connectionOptions = useMemo(() => {
    const apps = activeApps || []
    return buildConnectionOptions(apps, buildActionConnectionAccountLabels(apps, userMap))
  }, [activeApps, userMap])

  const selectedConnectionActionOptions = useMemo(() => {
    if (!orgAppUuid) return []
    const app = (activeApps || []).find((a) => a.id === orgAppUuid)
    return buildActionOptionsForConnection(app, encodeActionIdentifier)
  }, [activeApps, orgAppUuid])

  const handleConnectionSelect = useCallback(
    (connectionId: string) => {
      const connection = (activeApps || []).find((app) => app.id === connectionId)
      if (!connection) return

      form.setFieldValue(`actions[${index}].org_app_uuid`, connectionId)
      form.setFieldValue(`actions[${index}].app_provider`, connection.provider)
      // The previously selected action belongs to the old connection.
      form.setFieldValue(`actions[${index}].action_name`, null)
      form.setFieldValue(`actions[${index}].action_inputs`, {})
      form.setFieldValue(`actions[${index}].action_schema`, null)
      form.validateField(`actions[${index}].action_name`, 'change')
    },
    [activeApps, form, index],
  )

  const handleActionSelect = useCallback(
    (selectedActionId: string) => {
      const decoded = decodeActionIdentifier(selectedActionId)
      if (!decoded) return
      const { actionName, appId } = decoded
      const appWithAction = (activeApps || []).find((app) => app.id === appId)
      if (!appWithAction) return

      form.setFieldValue(`actions[${index}].org_app_uuid`, appId)
      form.setFieldValue(`actions[${index}].action_name`, actionName)
      form.setFieldValue(`actions[${index}].app_provider`, appWithAction.provider)
      form.setFieldValue(`actions[${index}].action_inputs`, {})

      // Populate action_schema from the tool's input_schema
      const { schema } = extractFieldsAndSchemaFromAction(actionName, activeApps, appId)
      form.setFieldValue(
        `actions[${index}].action_schema`,
        (schema || null) as Record<string, unknown> | null,
      )
      // Revalidate action_name since setFieldValue doesn't trigger onChange validators
      form.validateField(`actions[${index}].action_name`, 'change')
    },
    [activeApps, form, index],
  )

  if (!actionExists) return null

  const displayValue = (() => {
    if (typeof actionName === 'string' && orgAppUuid) {
      return encodeActionIdentifier(actionName, orgAppUuid)
    }
    if (typeof actionName === 'string' && appProvider && !orgAppUuid) {
      const appByProvider = findActionConnectionForSavedProvider(activeApps, appProvider)
      if (appByProvider) {
        return encodeActionIdentifier(actionName, appByProvider.id)
      }
    }
    return actionName ?? ''
  })()

  const actionFields: GenericFieldConfig<FormValues> = [
    {
      name: `actions[${index}].action_type`,
      label: '',
      componentType: 'select',
      zodValidation: z.enum(['EXECUTE_ACTION', 'CUSTOM_CODE', 'HTTP'], {
        message: 'Invalid action type',
      }),
      inputProps: {
        options: ACTION_TYPE_OPTIONS,
        placeholder: 'Select action type',
      },
    },
    // EXECUTE_ACTION fields
    {
      name: `actions[${index}].org_app_uuid`,
      type: 'custom',
      onChangeListenTo: [`actions[${index}].action_type`],
      visible: (values) => values.actions?.[index]?.action_type === 'EXECUTE_ACTION',
      component: () => (
        <div className="flex flex-col gap-2">
          <span className="text-s-500 text-primary">Select connection</span>
          <Select
            options={connectionOptions}
            loading={isLoadingApps}
            searchable
            placeholder="Select connection"
            emptyPlaceholder="No connections found"
            value={orgAppUuid ?? ''}
            onChange={(value) => handleConnectionSelect(String(value) || '')}
            data-testid="select-action-connection"
          />
        </div>
      ),
    },
    {
      name: `actions[${index}].action_name`,
      type: 'custom',
      onChangeListenTo: [`actions[${index}].action_type`, `actions[${index}].org_app_uuid`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'EXECUTE_ACTION'),
      zodValidation: z.string({ invalid_type_error: 'Action name is required' }).min(1, {
        message: 'Action name is required',
      }),
      component: () => (
        <div className="flex flex-col gap-2">
          <span className="text-s-500 text-primary">Select action</span>
          <Select
            options={selectedConnectionActionOptions}
            loading={isLoadingApps}
            searchable
            disabled={!orgAppUuid}
            placeholder={orgAppUuid ? 'Select action' : 'Select a connection first'}
            emptyPlaceholder="This connection has no actions"
            value={displayValue}
            onChange={(value) => handleActionSelect(String(value) || '')}
            data-testid="select-action"
          />
        </div>
      ),
    },
    {
      name: `actions[${index}].action_instructions`,
      type: 'sub-item',
      componentType: 'textarea',
      onChangeListenTo: [`actions[${index}].action_type`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'EXECUTE_ACTION'),
      zodValidation: z.string({ invalid_type_error: 'Action instructions are required' }).min(1, {
        message: 'Action instructions are required',
      }),
      inputProps: {
        label: 'Action instructions',
        placeholder: 'Provide information and describe when this action should be executed.',
        className: 'min-h-[120px] bg-background',
        'data-testid': 'textarea-action-instructions',
        id: `ai-agent-v2-action-instructions-${index}`,
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            inputElementSelector={`[name="actions[${index}].action_instructions"]`}
          />
        ),
      },
    },
    // CUSTOM_CODE fields
    {
      name: `actions[${index}].code_name`,
      type: 'sub-item',
      componentType: 'input',
      onChangeListenTo: [`actions[${index}].action_type`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'CUSTOM_CODE'),
      zodValidation: z.string({ invalid_type_error: 'Function name is required' }).min(1, {
        message: 'Function name is required',
      }),
      eventDataExtractor: (e) =>
        z
          .string()
          .transform((val) => val.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase())
          .parse(e.target?.value),
      inputProps: {
        placeholder: 'Enter function name',
        'data-testid': 'input-function-name',
      },
    },
    {
      name: `actions[${index}].code_description`,
      type: 'sub-item',
      componentType: 'textarea',
      onChangeListenTo: [`actions[${index}].action_type`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'CUSTOM_CODE'),
      zodValidation: z.string({ invalid_type_error: 'Function description is required' }).min(1, {
        message: 'Function description is required',
      }),
      inputProps: {
        label: 'Function description',
        placeholder: 'Describe what this function does and when to use it.',
        className: 'min-h-[120px] bg-background',
        id: `code-function-description-${index}`,
        'data-testid': 'textarea-function-description',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            inputElementSelector={`[name="actions[${index}].code_description"]`}
          />
        ),
      },
    },
    {
      name: `actions[${index}].code_body`,
      type: 'custom',
      onChangeListenTo: [`actions[${index}].action_type`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'CUSTOM_CODE'),
      eventDataExtractor: (data) => data,
      zodValidation: z.string({ invalid_type_error: 'Code is required' }).min(1, {
        message: 'Code is required',
      }),
      component: ({ onChange, value }) => (
        <div className="-mt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-s-600 text-secondary">
              Code <span className="text-s-400 italic text-tertiary">(Python3)</span>
            </span>
            <div className="flex items-center gap-1">
              <ClipboardCopyButton
                textToCopy={value as string}
                tooltipText="Copy code"
                className="size-4 text-secondary"
              />
              <Popover open={isSecretVarsOpen} onOpenChange={setIsSecretVarsOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-s-600 text-secondary p-1 hover:bg-accent rounded-sm data-[state=open]:bg-primary data-[state=open]:text-primary-foreground"
                    aria-label="View secret variables"
                    data-testid={`button-secret-variables-${index}`}
                  >
                    <Tooltip content="Add secret variables">
                      <SquareAsterisk className="w-4 h-4" />
                    </Tooltip>
                  </button>
                </PopoverTrigger>
                {isSecretVarsOpen && (
                  <SecretVariablesContent
                    // biome-ignore lint/suspicious/noExplicitAny: form types are structurally compatible
                    form={form as any}
                    arrayIndex={index}
                    onClose={() => setIsSecretVarsOpen(false)}
                  />
                )}
              </Popover>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => setIsCodeGenModalOpen(true)}
                aria-label="Generate code"
                className="form-button"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generate code
              </Button>
            </div>
          </div>
          <CodeEditor
            language="python"
            value={value as string}
            onChange={onChange}
            className="border border-st2 rounded-md overflow-auto"
            data-testid="code-editor"
          />
          <div className="mt-2">
            <ButtonWithLoader
              loading={isGeneratingFunctionSchema}
              variant="outline"
              type="button"
              size="sm"
              onClick={() => {
                const currentValue = form.state.values.actions?.[index]
                if (currentValue) {
                  generateFunctionSchema(
                    {
                      code: currentValue.code_body || '',
                      function_name: currentValue.code_name || '',
                      function_description: currentValue.code_description || '',
                    },
                    {
                      onSuccess: (data) => {
                        const currentIndex = getCurrentActionIndex()
                        if (currentIndex < 0) return
                        form.setFieldValue(
                          `actions[${currentIndex}].code_function_schema`,
                          data.function_schema,
                        )
                        form.validateField(
                          `actions[${currentIndex}].code_function_schema`,
                          'change',
                        )
                      },
                      onError: (error) => {
                        errorToast({
                          title: 'Failed to generate function schema',
                          description: getErrorText(error as AxiosError),
                        })
                      },
                    },
                  )
                }
              }}
            >
              Generate Function Schema
            </ButtonWithLoader>
          </div>
        </div>
      ),
    },
    {
      name: `actions[${index}].code_function_schema`,
      type: 'custom',
      onChangeListenTo: [`actions[${index}].action_type`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'CUSTOM_CODE'),
      eventDataExtractor: (data) => data,
      zodValidation: z
        .any()
        .refine((value) => value && typeof value === 'object' && Object.keys(value).length > 0, {
          message: 'Function schema is required. Please generate.',
        }),
      component: ({ value, onChange }) => {
        if (!value) return null
        const jsonValue = JSON.stringify(value, null, 2)
        return (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-s-600 text-secondary">
                Function Schema <span className="text-s-400 italic text-tertiary">(JSON)</span>
              </span>
              <ClipboardCopyButton
                textToCopy={jsonValue}
                tooltipText="Copy function schema"
                className="mr-1 size-4 text-secondary"
              />
            </div>
            <CodeEditor
              language="json"
              value={jsonValue}
              className="border border-st2 rounded-md overflow-auto bg-background"
              onChange={(code) => {
                try {
                  onChange(JSON.parse(code))
                } catch (_e) {
                  onChange(code)
                }
              }}
            />
          </div>
        )
      },
    },
    // HTTP action fields
    {
      name: `actions[${index}].http_tool_name`,
      type: 'sub-item',
      componentType: 'input',
      onChangeListenTo: [`actions[${index}].action_type`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'HTTP'),
      zodValidation: z.string({ invalid_type_error: 'Tool name is required' }).min(1, {
        message: 'Tool name is required',
      }),
      eventDataExtractor: (e) =>
        z
          .string()
          .transform((val) => val.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase())
          .parse(e.target?.value),
      inputProps: {
        label: 'Tool name',
        placeholder: 'e.g check_order_status',
        'data-testid': 'input-http-tool-name',
      },
    },
    {
      name: `actions[${index}].http_tool_description`,
      type: 'sub-item',
      componentType: 'textarea',
      onChangeListenTo: [`actions[${index}].action_type`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'HTTP'),
      zodValidation: z.string({ invalid_type_error: 'Tool description is required' }).min(1, {
        message: 'Tool description is required',
      }),
      inputProps: {
        label: 'Tool Description',
        placeholder: 'Describe when this HTTP request should be made and what it does.',
        className: 'min-h-[80px] bg-background',
        'data-testid': 'textarea-http-tool-description',
        id: `ai-agent-v2-http-tool-description-${index}`,
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            inputElementSelector={`[name="actions[${index}].http_tool_description"]`}
          />
        ),
      },
    },
    // API URL — inline method dropdown + URL input
    {
      name: `actions[${index}].http_url`,
      type: 'custom',
      onChangeListenTo: [`actions[${index}].action_type`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'HTTP'),
      eventDataExtractor: (data) => data,
      zodValidation: z.string({ invalid_type_error: 'URL is required' }).min(1, {
        message: 'URL is required',
      }),
      component: ({ value, onChange }) => {
        // Form is the source of truth. Mirror it into local state so external
        // writes (e.g. cURL import resetting http_method) keep the dropdown in
        // sync — otherwise the dropdown can show a stale method while the form
        // submits another one.
        const formMethod = form.state.values.actions?.[index]?.http_method || 'get'
        const [method, setMethod] = useState<string>(formMethod)
        useEffect(() => {
          setMethod(formMethod)
        }, [formMethod])

        return (
          <div className="-mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-s-500 text-primary">API URL</span>
              <CurlImportButton form={form} index={index} />
            </div>
            <DropdownInput
              selectTriggerClassName="w-[100px] shrink-0"
              options={HTTP_METHOD_OPTIONS}
              selectedOption={method}
              onOptionChange={(v) => {
                setMethod(v)
                form.setFieldValue(
                  `actions[${index}].http_method`,
                  v as 'get' | 'post' | 'put' | 'patch' | 'delete',
                )
              }}
              value={typeof value === 'string' ? value : ''}
              onValueChange={onChange}
              inputMode="url"
              placeholder="Enter a URL to make HTTP request"
              selectTestId="select-http-method"
              inputTestId="input-http-url"
            />
          </div>
        )
      },
    },
    {
      name: `actions[${index}].http_auth_type`,
      type: 'sub-item',
      componentType: 'select',
      onChangeListenTo: [`actions[${index}].action_type`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'HTTP'),
      inputProps: {
        label: 'Auth Type',
        options: HTTP_AUTH_TYPE_OPTIONS,
        'data-testid': 'select-http-auth-type',
      },
    },
    {
      name: `actions[${index}].http_auth_username`,
      type: 'sub-item',
      componentType: 'input',
      onChangeListenTo: [`actions[${index}].action_type`, `actions[${index}].http_auth_type`],
      visible: (values) =>
        values.actions?.[index]?.action_type === 'HTTP' &&
        values.actions?.[index]?.http_auth_type === 'basic_auth',
      inputProps: {
        label: 'Username',
        placeholder: 'Enter username',
        'data-testid': 'input-http-auth-username',
      },
    },
    {
      name: `actions[${index}].http_auth_password`,
      type: 'sub-item',
      componentType: 'inputPassword',
      onChangeListenTo: [`actions[${index}].action_type`, `actions[${index}].http_auth_type`],
      visible: (values) =>
        values.actions?.[index]?.action_type === 'HTTP' &&
        values.actions?.[index]?.http_auth_type === 'basic_auth',
      inputProps: {
        label: 'Password',
        placeholder: 'Enter password',
        'data-testid': 'input-http-auth-password',
      },
    },
    {
      name: `actions[${index}].http_auth_token`,
      type: 'sub-item',
      componentType: 'input',
      onChangeListenTo: [`actions[${index}].action_type`, `actions[${index}].http_auth_type`],
      visible: (values) =>
        values.actions?.[index]?.action_type === 'HTTP' &&
        values.actions?.[index]?.http_auth_type === 'bearer_token',
      inputProps: {
        label: 'Bearer token',
        placeholder: 'Enter token',
        'data-testid': 'input-http-auth-token',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            inputElementSelector={`[name="actions[${index}].http_auth_token"]`}
          />
        ),
      },
    },
    {
      name: `actions[${index}].http_headers`,
      type: 'custom',
      onChangeListenTo: [`actions[${index}].action_type`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'HTTP'),
      eventDataExtractor: (data) => data,
      component: ({ value, onChange }) => {
        const hasValue = Boolean(value && value !== '{}' && value !== '')
        const [open, setOpen] = useState(hasValue)
        const [jsonMode, setJsonMode] = useState(false)

        // Auto-open when value is set externally (e.g. cURL import)
        useEffect(() => {
          if (hasValue) setOpen(true)
        }, [hasValue])

        return (
          <>
            <hr className="border-st2 -mt-[2px] mb-[14px]" />
            <div className="border border-st2 rounded-md overflow-hidden">
              <button
                type="button"
                className="flex items-center justify-between w-full px-3 py-2 text-s-500 text-primary bg-bg2 hover:bg-bg3"
                onClick={() => setOpen(!open)}
              >
                <div className="flex flex-col items-start">
                  <span>Headers</span>
                  <span className="text-xs-400 text-tertiary">
                    Define headers that will be sent with the request.
                  </span>
                </div>
                <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
              </button>
              {open && (
                <div className="p-3">
                  {jsonMode ? (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-jetbrains text-label uppercase text-tertiary">
                          JSON
                        </span>
                        <button
                          type="button"
                          className="font-inter text-xs-500 text-primary hover:underline"
                          onClick={() => setJsonMode(false)}
                        >
                          Key-Value
                        </button>
                      </div>
                      <CodeEditor
                        language="json"
                        value={
                          typeof value === 'string' ? value : JSON.stringify(value || {}, null, 2)
                        }
                        onChange={(code) => onChange(code)}
                        suggestions={requestVars}
                        className="border border-st2 rounded-md overflow-auto min-h-[60px]"
                      />
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-inter text-xs-500 text-tertiary">Key / Value</span>
                        <button
                          type="button"
                          className="font-inter text-xs-500 text-primary hover:underline"
                          onClick={() => setJsonMode(true)}
                        >
                          Bulk Edit
                        </button>
                      </div>
                      <JsonKeyValueEditor
                        showSecretVariables
                        showJsonModeToggle={false}
                        initialData={parseJsonData(
                          typeof value === 'string' ? value : JSON.stringify(value || {}),
                        )}
                        nodeVars={requestVars}
                        keyVars={nodeVars}
                        onChange={(newHeaders) => onChange(JSON.stringify(newHeaders))}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )
      },
    },
    // Params — collapsible key-value section for query parameters
    {
      name: `actions[${index}].http_params`,
      type: 'custom',
      onChangeListenTo: [`actions[${index}].action_type`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'HTTP'),
      eventDataExtractor: (data) => data,
      component: ({ value, onChange }) => {
        const hasParams = Boolean(value && value !== '{}' && value !== '')
        const [open, setOpen] = useState(hasParams)

        useEffect(() => {
          if (hasParams) setOpen(true)
        }, [hasParams])
        return (
          <div className="-mt-2 border border-st2 rounded-md overflow-hidden">
            <button
              type="button"
              className="flex items-center justify-between w-full px-3 py-2 text-s-500 text-primary bg-bg2 hover:bg-bg3"
              onClick={() => setOpen(!open)}
            >
              <div className="flex flex-col items-start">
                <span>Params</span>
                <span className="text-xs-400 text-tertiary">
                  Add query parameters that will be appended to the request URL.
                </span>
              </div>
              <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
            </button>
            {open && (
              <div className="p-3">
                <JsonKeyValueEditor
                  showSecretVariables
                  showJsonModeToggle={false}
                  initialData={parseJsonData(
                    typeof value === 'string' ? value : JSON.stringify(value || {}),
                  )}
                  nodeVars={requestVars}
                  keyVars={nodeVars}
                  onChange={(newParams) => onChange(JSON.stringify(newParams))}
                />
              </div>
            )}
          </div>
        )
      },
    },
    // Body — collapsible section with type selector + editor
    {
      name: `actions[${index}].http_body_template`,
      type: 'custom',
      onChangeListenTo: [`actions[${index}].action_type`, `actions[${index}].http_body_type`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'HTTP'),
      eventDataExtractor: (data) => data,
      component: ({ value, onChange }) => {
        const formBodyType = form.state.values.actions?.[index]?.http_body_type || 'none'
        const [open, setOpen] = useState(formBodyType !== 'none')
        const [bodyType, setBodyType] = useState<string>(formBodyType)

        // Sync local state when form value changes (e.g. cURL import)
        useEffect(() => {
          setBodyType(formBodyType)
          if (formBodyType !== 'none') setOpen(true)
        }, [formBodyType])
        return (
          <div className="-mt-2 border border-st2 rounded-md overflow-hidden">
            <button
              type="button"
              className="flex items-center justify-between w-full px-3 py-2 text-s-500 text-primary bg-bg2 hover:bg-bg3"
              onClick={() => setOpen(!open)}
            >
              <div className="flex flex-col items-start">
                <span>Body</span>
                <span className="text-xs-400 text-tertiary">
                  Specify the payload to be sent in the request body.
                </span>
              </div>
              <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
            </button>
            {open && (
              <div className="p-3 flex flex-col gap-3">
                <Select
                  label="Type"
                  options={HTTP_BODY_TYPE_OPTIONS}
                  value={bodyType}
                  onChange={(val) => {
                    const v = typeof val === 'string' ? val : val[0]
                    setBodyType(v)
                    form.setFieldValue(
                      `actions[${index}].http_body_type`,
                      v as 'none' | 'json' | 'form_data' | 'form_urlencoded',
                    )
                  }}
                />
                {bodyType === 'json' && (
                  <CodeEditor
                    language="json"
                    value={typeof value === 'string' ? value : JSON.stringify(value || {}, null, 2)}
                    onChange={(code) => onChange(code)}
                    suggestions={requestVars}
                    className="border border-st2 rounded-md overflow-auto min-h-[120px]"
                  />
                )}
                {(bodyType === 'form_data' || bodyType === 'form_urlencoded') && (
                  <JsonKeyValueEditor
                    showSecretVariables
                    showJsonModeToggle={false}
                    initialData={parseJsonData(
                      typeof value === 'string' ? value : JSON.stringify(value || {}),
                    )}
                    nodeVars={requestVars}
                    keyVars={nodeVars}
                    onChange={(newBody) => onChange(JSON.stringify(newBody))}
                  />
                )}
              </div>
            )}
          </div>
        )
      },
    },
    {
      name: `actions[${index}].http_function_schema`,
      type: 'custom',
      onChangeListenTo: [`actions[${index}].action_type`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'HTTP'),
      eventDataExtractor: (data) => data,
      component: ({ value, onChange }) => {
        const schemaJson =
          typeof value === 'string'
            ? value
            : JSON.stringify(value || { type: 'object', properties: {}, required: [] }, null, 2)
        return (
          <div className="-mt-2 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-s-500 text-primary">
                Tool parameter schema <span className="text-xsi-400 text-tertiary">(JSON)</span>
              </span>
              <div className="flex items-center gap-2">
                <ClipboardCopyButton
                  textToCopy={schemaJson}
                  tooltipText="Copy schema"
                  className="size-4 text-secondary"
                />
                <ButtonWithLoader
                  loading={isGeneratingHttpFunctionSchema}
                  variant="outline"
                  type="button"
                  size="sm"
                  onClick={() => {
                    const currentValue = form.state.values.actions?.[index]
                    if (currentValue) {
                      generateHttpFunctionSchema(
                        {
                          tool_name: currentValue.http_tool_name || '',
                          tool_description: currentValue.http_tool_description || '',
                          http_method: currentValue.http_method || 'post',
                          http_url: currentValue.http_url || '',
                          http_headers: currentValue.http_headers,
                          http_body_type: currentValue.http_body_type || 'none',
                          http_body_template: currentValue.http_body_template,
                        },
                        {
                          onSuccess: (data) => {
                            const currentIndex = getCurrentActionIndex()
                            if (currentIndex < 0) return
                            const values = form.state.values
                            const action = values.actions?.[currentIndex]
                            form.setFieldValue(
                              `actions[${currentIndex}].http_function_schema`,
                              dropOwnVariableSlots(
                                data.function_schema,
                                ownVariableNames(values, ownVariables),
                                action ?? {},
                              ),
                            )
                            form.validateField(
                              `actions[${currentIndex}].http_function_schema`,
                              'change',
                            )
                          },
                          onError: (error) => {
                            errorToast({
                              title: 'Failed to generate schema',
                              description: getErrorText(error as AxiosError),
                            })
                          },
                        },
                      )
                    }
                  }}
                >
                  <Sparkles className="size-3 mr-1" />
                  Generate Schema
                </ButtonWithLoader>
              </div>
            </div>
            <CodeEditor
              language="json"
              value={schemaJson}
              onChange={(code) => {
                try {
                  onChange(JSON.parse(code))
                } catch (_e) {
                  onChange(code)
                }
              }}
              className="border border-st2 rounded-md overflow-auto min-h-[120px]"
            />
            <hr className="border-st2 mt-[6px] mb-[-2px]" />
          </div>
        )
      },
    },
    {
      name: `actions[${index}].http_test_response`,
      type: 'custom',
      onChangeListenTo: [`actions[${index}].action_type`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'HTTP'),
      eventDataExtractor: (data) => data,
      component: ({ value, onChange }) => {
        const [testing, setTesting] = useState(false)
        const hasResponse = Boolean(value && value !== '{}' && value !== '')

        const handleTestRequest = async () => {
          setTesting(true)
          try {
            const action = form.state.values.actions?.[index]
            if (!action) return

            // Map AI agent action fields to http_request format
            const authParams: Record<string, string> = {}
            if (action.http_auth_type === 'basic_auth') {
              authParams.username = action.http_auth_username || ''
              authParams.password = action.http_auth_password || ''
            } else if (action.http_auth_type === 'bearer_token') {
              authParams.token = action.http_auth_token || ''
            }

            const payload = {
              url: (action.http_url || '').trim(),
              method: (action.http_method || 'post').toLowerCase(),
              auth_type: action.http_auth_type || 'no_auth',
              auth_params: JSON.stringify(authParams),
              headers: action.http_headers || '{}',
              body_type: action.http_body_type || 'none',
              body:
                action.http_body_template && action.http_body_type !== 'none'
                  ? action.http_body_template
                  : '{}',
              params: '{}',
              timeout_ms: Math.max(action.http_timeout_ms || 5000, 5000),
              retry_limit: action.http_retry_limit || 0,
            }

            const resp = await flowService.execHttpRequest(payload)
            const responseFormat = JSON.stringify(resp.data.response_format, null, 2)
            onChange(responseFormat)
          } catch (error) {
            const axiosError = error as AxiosError
            const errorObject = axiosError.response?.data || axiosError.response || axiosError
            try {
              onChange(
                JSON.stringify(
                  typeof errorObject === 'string' ? { error: errorObject } : errorObject,
                  null,
                  2,
                ),
              )
            } catch (_e) {
              onChange(JSON.stringify({ error: 'Unknown error occurred' }, null, 2))
            }
          } finally {
            setTesting(false)
          }
        }

        return (
          <div className="flex flex-col gap-3">
            {/* Console only: the test runs server-side (exec_http) and can attach an org
                OAuth token to a caller-chosen URL, so it is not on the embed allow-list. */}
            {!isEmbedMode() && (
              <>
                <span className="text-s-500 text-primary">Test API request</span>
                <ButtonWithLoader
                  loading={testing}
                  variant="outline"
                  type="button"
                  size="sm"
                  className="w-fit"
                  onClick={handleTestRequest}
                >
                  <ArrowLeftRight className="size-3 mr-1" />
                  {hasResponse ? 'Retest' : 'Test Response'}
                </ButtonWithLoader>
              </>
            )}
            {hasResponse && (
              <div className="flex flex-col gap-1">
                <span className="font-inter text-s-500 text-secondary">Response</span>
                <CodeEditor
                  language="json"
                  value={typeof value === 'string' ? value : ''}
                  onChange={(code) => onChange(code)}
                  className="border border-st2 rounded-md overflow-auto min-h-[80px]"
                />
              </div>
            )}
          </div>
        )
      },
    },
    {
      name: `actions[${index}].http_timeout_ms`,
      type: 'sub-item',
      componentType: 'inputNumber',
      onChangeListenTo: [`actions[${index}].action_type`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'HTTP'),
      inputProps: {
        label: 'Time out requests after',
        placeholder: '5000',
        min: 0,
        max: 30000,
        endIcon: <span className="font-jetbrains text-xs-400 text-tertiary">msecs</span>,
        'data-testid': 'input-http-timeout',
      },
    },
    {
      name: `actions[${index}].http_retry_limit`,
      type: 'sub-item',
      componentType: 'inputNumber',
      onChangeListenTo: [`actions[${index}].action_type`],
      visible: (values) => Boolean(values.actions?.[index]?.action_type === 'HTTP'),
      inputProps: {
        label: 'Retry the request',
        placeholder: '0',
        min: 0,
        max: 5,
        endIcon: <span className="font-jetbrains text-xs-400 text-tertiary">times</span>,
        'data-testid': 'input-http-retry-limit',
      },
    },
  ]

  const collapsedDescription = getCollapsedDescription()
  const isNoActionSelected = actionType === 'EXECUTE_ACTION' && !actionName
  const displayName = getActionDisplayName(actionType, actionName, codeName, httpToolName, index)

  return (
    <div
      className={cn(
        'relative rounded-xl border flex flex-col',
        isExpanded
          ? 'bg-bg2 p-4 gap-4 has-[.field-error-message]:border-destructive'
          : 'p-3 cursor-pointer hover:bg-bg2',
        isNoActionSelected && !isExpanded && 'border-destructive/50',
      )}
      onClick={
        !isExpanded ? () => form.setFieldValue(`actions[${index}].is_expanded`, true) : undefined
      }
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-0 pr-16">
          {isNoActionSelected && !isExpanded ? (
            <span className="text-destructive">⚠</span>
          ) : (
            getActionIcon(actionType || '', appLogoUrl)
          )}
          <div className="flex flex-col min-w-0">
            <span
              className={cn(
                'text-p-500 truncate',
                isNoActionSelected && !isExpanded ? 'text-primary' : 'text-secondary',
              )}
            >
              {displayName}
            </span>
            {!isExpanded && collapsedDescription && (
              <span
                className={cn(
                  'text-xs-400 truncate',
                  isNoActionSelected ? 'text-destructive' : 'text-tertiary',
                )}
              >
                {collapsedDescription}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 absolute right-3 top-3">
          <button
            type="button"
            aria-label="Remove action"
            data-testid={`button-remove-action-${index}`}
            className="text-destructive hover:text-destructive/80"
            onClick={(e) => {
              e.stopPropagation()
              field.removeValue(index)
            }}
          >
            <Trash2 size={18} strokeWidth={1.5} />
          </button>
          {isExpanded ? (
            <button
              type="button"
              data-testid={`button-toggle-action-${index}`}
              className="text-primary hover:text-primary/80"
              onClick={() => form.setFieldValue(`actions[${index}].is_expanded`, false)}
              aria-label="Collapse action"
            >
              <Minus className="h-5 w-5" strokeWidth={2.5} />
            </button>
          ) : (
            <button
              type="button"
              data-testid={`button-expand-action-${index}`}
              className="text-primary hover:text-primary/80"
              onClick={(e) => {
                e.stopPropagation()
                form.setFieldValue(`actions[${index}].is_expanded`, true)
              }}
              aria-label="Expand action"
            >
              <Plus className="h-5 w-5" strokeWidth={2.5} />
            </button>
          )}
        </div>
      </div>

      <div className={cn('flex flex-col gap-4', !isExpanded && 'hidden')}>
        <FieldsRenderer<FormValues>
          form={form}
          fields={actionFields}
          baseName={`actions[${index}]`}
          arrayIndex={index}
        />
      </div>

      <CodeGenerationModal
        isOpen={isCodeGenModalOpen}
        onClose={() => setIsCodeGenModalOpen(false)}
        // biome-ignore lint/suspicious/noExplicitAny: form types are structurally compatible (both have actions array)
        form={form as any}
        arrayIndex={index}
        onCodeGenerated={(
          code: string,
          schema: Record<string, unknown>,
          name: string,
          description: string,
        ) => {
          const currentIndex = getCurrentActionIndex()
          if (currentIndex < 0) return
          form.setFieldValue(`actions[${currentIndex}].code_body`, code)
          form.setFieldValue(`actions[${currentIndex}].code_name`, name)
          form.setFieldValue(`actions[${currentIndex}].code_description`, description)
          form.setFieldValue(`actions[${currentIndex}].code_function_schema`, schema)
          form.validateAllFields('change')
        }}
      />
    </div>
  )
}
